import React, { useState } from 'react';
import './css/index.scss';
import MyButton from '../../../../components/MyButton';
import MyModal from '../../../../components/MyModal';
import sendRequest from '../../../../helpers/sendRequest';
import Api from '../../../../Api';
import {
  buildEventCategoryBulkPublishFileContent,
  fetchPendingCategoryBulkPublishData,
  formatBulkCategoryPublishSuccessMessage,
  parseBulkCategoryPublishItems,
} from '../../../../helpers/eventCategoryBulkPublish';

function BulkEventCategoryInstructions() {
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [localError, setLocalError] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalState, setModalState] = useState('');

  const onDownloadFile = () => {
    setLocalError('');
    setDownloadLoading(true);
    fetchPendingCategoryBulkPublishData()
      .then(({ events, categories }) => {
        if (!events.length) {
          setLocalError('Нет мероприятий без категории: список пуст. Файл всё равно можно скачать для проверки справочника категорий.');
        }
        const content = buildEventCategoryBulkPublishFileContent({ events, categories });
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'nomad-events-pending-category-ai.txt';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e) => {
        const msg = e?.message || 'Не удалось загрузить данные. Нужны права canEditEvents для списка на модерации.';
        setLocalError(msg);
      })
      .finally(() => setDownloadLoading(false));
  };

  const onCloseModal = () => {
    setIsModalVisible(false);
    setTimeout(() => {
      setModalText('');
      setModalState('');
    }, 400);
  };

  const onSubmit = async () => {
    setLocalError('');
    const parsed = parseBulkCategoryPublishItems(responseText);
    if (parsed.error) {
      setLocalError(parsed.error);
      return;
    }

    const data = await sendRequest({
      request: Api.bulkCategoryPublishEvents,
      payload: { items: parsed.items },
      warnErrorText: 'while bulk category publish',
      setLoading: setSubmitLoading,
    });

    setIsModalVisible(true);
    if (data?.status !== 'ok') {
      const errorText = data?.message || 'Не удалось отправить данные на сервер.';
      setModalText(`Возникла ошибка:\n\n${errorText}`);
      setModalState('error');
      return;
    }
    setModalText(formatBulkCategoryPublishSuccessMessage(data));
    setModalState('success');
    setResponseText('');
  };

  return (
    <div className="bulk_event_category_instructions">
      <div className="bulk_event_category_instructions_left">
        <div className="bulk_event_category_instructions_text">
          <p>
            <strong>Шаг 1.</strong>
            {' '}
            Нажмите «Скачать файл для ИИ». Запросятся категории афиш и мероприятия без категории
            (скрытые и на модерации, в т.ч. с парсинга). В файле есть блок{' '}
            <code>ОЖИДАЕМЫЙ_ФОРМАТ_ОТВЕТА</code>
            {' '}
            — пример JSON с полями
            {' '}
            <code>event_id</code>
            ,
            {' '}
            <code>events_category_id</code>
            ,
            {' '}
            <code>events_category_name</code>
            ,
            {' '}
            <code>event_name</code>
            .
          </p>
          <p>
            <strong>Шаг 2.</strong>
            {' '}
            Одним сообщением отправьте в чат с моделью весь файл. Модель должна сразу сгенерировать и прислать только JSON с ключом
            {' '}
            <code>items</code>
            , без пояснений до и после.
          </p>
          <p>
            <strong>Шаг 3.</strong>
            {' '}
            Вставьте этот JSON в поле справа и нажмите «Отправить на сервер».
            Выполнится пакетное назначение категории и публикация
            {' '}
            (
            <code>POST /api/admins/events/bulk-category-publish</code>
            ). Нужны права
            {' '}
            <code>canCallOperations</code>
            .
          </p>
          <p>
            Если ответ ИИ не принимается из‑за неверной структуры или синтаксиса JSON, откройте
            {' '}
            <a
              href="https://jsonviewer.stack.hu/"
              target="_blank"
              rel="noopener noreferrer"
              className="bulk_event_category_instructions_link"
            >
              jsonviewer.stack.hu
            </a>
            , вставьте текст и нажмите «Format» — так проще увидеть ошибку и выровнять структуру; затем скопируйте результат в поле справа.
          </p>
          <p>
            Скачивание списка мероприятий требует права
            {' '}
            <code>canEditEvents</code>
            . Скрытые афиши (
            <code>is_hidden: true</code>
            ) в обычном каталоге не показываются, пока не будут обработаны.
          </p>
        </div>
        <MyButton
          text="Скачать файл для ИИ"
          onClick={onDownloadFile}
          loading={downloadLoading}
          className="bulk_event_category_instructions_download_button"
        />
        {localError ? (
          <p className="bulk_event_category_instructions_error">{localError}</p>
        ) : null}
      </div>

      <div className="bulk_event_category_instructions_right">
        <div className="bulk_event_category_instructions_submit_block">
          <h3 className="bulk_event_category_instructions_submit_title">Ответ модели (JSON)</h3>
          <p className="bulk_event_category_instructions_submit_description">
            Вставьте сюда ответ модели: один JSON с ключом
            {' '}
            <code>items</code>
            {' '}
            (как в примере из файла — с полями для сверки).
          </p>
          <textarea
            className="bulk_event_category_instructions_textarea"
            placeholder="Вставьте JSON сюда…"
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            rows={16}
            spellCheck={false}
          />
          <MyButton
            text="Отправить на сервер"
            onClick={onSubmit}
            loading={submitLoading}
            className="bulk_event_category_instructions_submit_button"
          />
        </div>
      </div>

      <MyModal isOpen={isModalVisible} closeFunction={onCloseModal}>
        <div style={{ padding: '20px' }}>
          <p style={{ whiteSpace: 'pre-wrap', marginBottom: '20px' }}>{modalText}</p>
          <MyButton
            text={modalState === 'error' ? 'Закрыть' : 'ОК'}
            onClick={onCloseModal}
          />
        </div>
      </MyModal>
    </div>
  );
}

export default BulkEventCategoryInstructions;
